// @ts-nocheck
// The planner block (docs/PLANNER_BLOCK.md, snippets/vhp-planner-block.liquid, styles assets/vhp-planner-block.css).
// - The demo runs while at least half of it is on screen (it pauses off screen and in a hidden tab), from the first
//   frame on; reduced motion and Save-Data keep the finished week with every line lit.
// - A tap on the demo is the button; a tap on a caption line jumps the demo to its beat.
// - The collection tile (the mini block) opens the block as a sheet for someone who hasn't started the planner;
//   someone who has goes straight in. (The recipe's planner line that did the same is gone since 2026-11-03.)
// - The button itself is handled by assets/vhp-plan-goal.js ([data-vhp-plan-cta] + data-vhp-plan-direct: like, pick,
//   planner_cta_click, ?src=next = the recipe fast path).
// Events: planner_block <what>_<door> (seen, play3, loop, tap, line, cta, sheet; door = data-vpb-at), assets/vhp-planner-events.js.
(() => {
  'use strict';

  if (window.vhpPlannerBlock) return;
  window.vhpPlannerBlock = true;

  const LOOP_MS = 16000;
  // Where each caption's beat starts (ms), as in the CSS timeline.
  const BEATS = [0, 3500, 7000, 10500];
  // Film mode (snippets/vhp-planner-film.liquid): the opening built in the page runs this long, then the video.
  const INTRO_MS = 6400;

  const still = () => {
    try {
      return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData);
    } catch (e) {
      return false;
    }
  };

  const states = new WeakMap();
  const stateOf = (block) => {
    let st = states.get(block);
    if (!st) {
      st = { on: false, visible: false, since: 0, played: 0, timer: 0, sent: new Set() };
      states.set(block, st);
    }
    return st;
  };

  const send = (block, what) => {
    window.vhpPlannerEvents?.step('planner_block', `${what}_${block.dataset.vpbAt || 'end'}`);
  };
  const sendOnce = (block, what) => {
    const st = stateOf(block);
    if (st.sent.has(what)) return;
    st.sent.add(what);
    send(block, what);
  };

  // Play time on screen, for play3 (3 s) and loop (one whole loop).
  const count = (block) => {
    const st = stateOf(block);
    if (!st.since) return;
    const now = performance.now();
    st.played += now - st.since;
    st.since = now;
    if (st.played >= 3000) sendOnce(block, 'play3');
    // the film counts its loop when the video ends (filmVideoStart)
    if (st.played >= LOOP_MS && !filmOf(block)) sendOnce(block, 'loop');
  };

  // ---------- film mode: the opening (CSS, .is-run), then the video (.is-video), then the opening again ----------
  const filmOf = (block) => block.querySelector('[data-vpf]');
  const filmVideo = (film) => film.querySelector('[data-vpf-video]');
  const loadFilm = (film) => {
    const video = filmVideo(film);
    if (video && !video.getAttribute('src') && video.dataset.src) {
      video.preload = 'auto';
      video.src = video.dataset.src;
    }
  };

  // Likes mode (the likes page): her latest liked main (else her latest like with a photo) on the big card.
  const paintFilmLikes = (film) => {
    if (!film.hasAttribute('data-vpf-likes')) return;
    const img = film.querySelector('[data-vpf-hers]');
    const api = window.vhpPlannerLikes;
    if (!img || !api) return;
    let items = [];
    try {
      items = api.likeItems(api.read()) || [];
    } catch (e) {
      items = [];
    }
    const withPhoto = [...items].reverse().filter((item) => item.image);
    const pick = withPhoto.find((item) => (item.slot || 'main') === 'main') || withPhoto[0];
    if (pick && img.getAttribute('src') !== pick.image) img.src = pick.image;
  };

  const filmIntro = (block, film) => {
    const st = stateOf(block);
    paintFilmLikes(film);
    // plain mode (a cut of the film, docs/PLANNER_VIDEO.md 15): no opening here, the video from its start
    if (!film.querySelector('.vpf__stage')) {
      filmVideoStart(block, film);
      return;
    }
    filmVideo(film)?.pause();
    film.classList.remove('is-run', 'is-video');
    void film.offsetWidth;
    film.classList.add('is-run');
    st.phase = 'intro';
    st.left = INTRO_MS;
    st.at = performance.now();
    window.clearTimeout(st.next);
    st.next = window.setTimeout(() => filmVideoStart(block, film), INTRO_MS);
  };

  const filmVideoStart = (block, film) => {
    const st = stateOf(block);
    const video = filmVideo(film);
    const plain = !film.querySelector('.vpf__stage');
    if (!video?.dataset.src) {
      if (!plain) filmIntro(block, film); // no video picked: the opening loops on its own
      return;
    }
    loadFilm(film);
    if (!video.dataset.vpfBound) {
      video.dataset.vpfBound = '1';
      video.addEventListener('ended', () => {
        sendOnce(block, 'loop');
        if (stateOf(block).filmOn) filmIntro(block, film);
      });
    }
    st.phase = 'video';
    try {
      video.currentTime = 0;
    } catch (e) {
      /* not loaded yet */
    }
    film.classList.add('is-video');
    video.play()?.catch?.(() => {
      // autoplay refused (Low Power Mode): stay on the opening, or on the poster in plain mode
      film.classList.remove('is-video');
      if (!plain) filmIntro(block, film);
    });
  };

  const filmPlay = (block, film) => {
    const st = stateOf(block);
    if (st.filmOn) return;
    st.filmOn = true;
    if (!st.phase) {
      filmIntro(block, film);
    } else if (st.phase === 'intro') {
      st.at = performance.now();
      window.clearTimeout(st.next);
      st.next = window.setTimeout(() => filmVideoStart(block, film), st.left);
    } else {
      filmVideo(film)?.play()?.catch?.(() => {});
    }
  };

  const filmPause = (block, film) => {
    const st = stateOf(block);
    if (!st.filmOn) return;
    st.filmOn = false;
    if (st.phase === 'intro') {
      window.clearTimeout(st.next);
      st.left = Math.max(0, st.left - (performance.now() - st.at));
    } else if (st.phase === 'video') {
      filmVideo(film)?.pause();
    }
  };

  const play = (block) => {
    const st = stateOf(block);
    if (still() || document.visibilityState === 'hidden') return;
    if (!st.on) {
      st.on = true;
      block.classList.add('is-on');
    }
    block.classList.remove('is-paused');
    const film = filmOf(block);
    if (film) filmPlay(block, film);
    if (!st.since) st.since = performance.now();
    window.clearInterval(st.timer);
    st.timer = window.setInterval(() => count(block), 500);
  };

  const pause = (block) => {
    const st = stateOf(block);
    count(block);
    st.since = 0;
    window.clearInterval(st.timer);
    if (st.on) block.classList.add('is-paused');
    const film = filmOf(block);
    if (film) filmPause(block, film);
  };

  // Restart every part of the timeline at a beat: same negative delay for all, one reflow.
  const jump = (block, beat) => {
    if (still()) return;
    const st = stateOf(block);
    const film = filmOf(block);
    if (film) {
      // the film starts over from its opening (the sheet opening again)
      filmPause(block, film);
      st.phase = '';
      play(block);
      return;
    }
    block.style.setProperty('--vpb-off', `-${(BEATS[beat] || 0) / 1000}s`);
    block.classList.remove('is-on', 'is-paused');
    void block.offsetWidth;
    st.on = false;
    play(block);
  };

  // The demo's photos are lazy, and the swap-in card and the list sit off to the side, where lazy images never load.
  // Once the block is a screen away, load them all (about 30 thumbnails at 160 px).
  let near = null;
  const warm = (block) => {
    block.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    const film = filmOf(block);
    if (film) {
      paintFilmLikes(film);
      loadFilm(film);
    }
  };

  let seen = null;
  const watch = (block) => {
    if (block.dataset.vpbBound) return;
    block.dataset.vpbBound = '1';
    const demo = block.querySelector('[data-vpb-demo]');
    if (!demo) return;
    if (!('IntersectionObserver' in window)) {
      play(block);
      return;
    }
    if (!seen) {
      seen = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            const b = entry.target.closest('[data-vpb]');
            if (!b) return;
            const st = stateOf(b);
            st.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
            if (st.visible) {
              window.vhpPlannerBlock?.paintSaved?.(b);
              sendOnce(b, 'seen');
              play(b);
            } else {
              pause(b);
            }
          }),
        { threshold: [0, 0.5, 1] }
      );
    }
    seen.observe(demo);
    if (!near) {
      near = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            near.unobserve(entry.target);
            const b = entry.target.closest('[data-vpb]');
            if (b) {
              window.vhpPlannerBlock?.paintSaved?.(b);
              warm(b);
            }
          }),
        { rootMargin: '100% 0px' }
      );
    }
    near.observe(demo);
  };

  // ---------- the tiles' loop (the mini block's text-free film): plays while half on screen ----------
  let loopSeen = null;
  const bindLoop = (video) => {
    if (video.dataset.vpfBound) return;
    video.dataset.vpfBound = '1';
    if (!('IntersectionObserver' in window)) return;
    if (!loopSeen) {
      loopSeen = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            const v = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !still() && document.visibilityState !== 'hidden') {
              if (!v.getAttribute('src') && v.dataset.src) v.src = v.dataset.src;
              v.play()?.catch?.(() => {});
            } else {
              v.pause();
            }
          }),
        { threshold: [0, 0.5, 1] }
      );
    }
    loopSeen.observe(video);
  };

  const bindAll = () => {
    document.querySelectorAll('[data-vpb]').forEach(watch);
    document.querySelectorAll('video[data-vpf-loop]').forEach(bindLoop);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  else bindAll();
  // Recipe bodies that arrive later (the recipe sheet loads its body by fetch).
  if ('MutationObserver' in window) {
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        bindAll();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('[data-vpb].is-on').forEach((block) => {
      if (document.visibilityState === 'hidden') pause(block);
      else if (stateOf(block).visible) play(block);
    });
  });

  // ---------- taps ----------
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const block = target?.closest('[data-vpb]');
    if (!block) return;
    const cta = block.querySelector('[data-vpb-cta]');

    if (target.closest('[data-vpb-demo]') && cta) {
      send(block, 'tap');
      block.dataset.vpbFromTap = '1';
      cta.click();
      return;
    }
    if (target.closest('[data-vpb-cta]')) {
      if (block.dataset.vpbFromTap) delete block.dataset.vpbFromTap;
      else send(block, 'cta');
      return;
    }
    const line = target.closest('[data-vpb-beat]');
    if (line) {
      send(block, 'line');
      jump(block, Number(line.dataset.vpbBeat) || 0);
    }
  });

  // ---------- the sheet from the planner line ----------
  // Started = the planner has their goal or got past its first screen (as assets/vhp-plan-goal.js started()).
  const started = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(window.vhpPlannerLikes?.STORAGE_KEY || 'vhp_planner_anon') || 'null');
      return Boolean(saved && (saved.goal || (saved.screen && saved.screen !== 'goal')));
    } catch (e) {
      return false;
    }
  };

  const setOpen = (sheet, open) => {
    if (window.vhpSheet) window.vhpSheet.setOpen(sheet, open);
    else if (open && !sheet.open) sheet.showModal();
    else if (!open && sheet.open) sheet.close();
  };

  const bindSheet = (sheet) => {
    if (sheet.dataset.vpbSheetBound) return;
    sheet.dataset.vpbSheetBound = '1';
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      setOpen(sheet, false);
    });
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet || (event.target instanceof Element && event.target.closest('[data-vpb-close]'))) setOpen(sheet, false);
    });
  };

  const openSheet = (sheet) => {
    bindSheet(sheet);
    const block = sheet.querySelector('[data-vpb]');
    setOpen(sheet, true);
    sheet.querySelector('.vpb-sheet__body')?.scrollTo(0, 0);
    sheet.querySelector('.vpb__title')?.focus({ preventScroll: true });
    if (block) {
      watch(block);
      send(block, 'sheet');
      // Start at the first beat each time the sheet opens.
      if (stateOf(block).on) jump(block, 0);
    }
  };

  // Capture on window, before the doors' own handlers (assets/vhp-plan-goal.js, assets/vhp-track.js, the router):
  // the sheet takes the tap, nothing else sees it. The collection tile (the mini block) opens the sheet it names.
  // Someone who started the planner goes straight in.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const mini = target?.closest('[data-vpb-mini]');
      if (!mini || started()) return;
      const sheet = document.getElementById(mini.dataset.vpbOpen || '');
      if (!sheet || !sheet.matches('[data-vpb-sheet]')) return;
      event.preventDefault();
      event.stopPropagation();
      // The menu card sits in the menu sheet (a popover): close it first, then the block's sheet comes up on its own.
      const host = mini.closest('[popover], dialog');
      if (host) {
        if (host.matches('[popover]')) {
          try {
            host.hidePopover();
          } catch (e) {
            /* already closed */
          }
        } else setOpen(host, false);
        window.setTimeout(() => openSheet(sheet), 180);
        return;
      }
      openSheet(sheet);
    },
    true
  );

  // ---------- the planner's first screen: "So funktioniert's · 15 s" (docs/PLANNER_VIDEO.md 15) ----------
  // The link opens the film sheet (sections/meal-planner.liquid); its button closes the sheet and does what the
  // screen's own "Meine Woche planen" does, so nobody leaves or reloads the page.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const sheet = document.getElementById('vpb-sheet-landing');
      if (!sheet) return;
      if (target.closest('[data-vpf-landing-open]')) {
        event.preventDefault();
        openSheet(sheet);
        return;
      }
      if (target.closest('#vpb-sheet-landing [data-vpb-cta]')) {
        event.preventDefault();
        setOpen(sheet, false);
        window.setTimeout(() => document.querySelector('[data-vhp-monday-go]')?.click(), 180);
      }
    },
    true
  );

  // ---------- her saved main on today's slot (the reel's planner slide) ----------
  // Her latest saved main (likes are taste: the planner pins up to two liked mains on her free day, so one of them on
  // today's lunch is what the week will look like). Painted each time the slide comes on screen. No main saved: the
  // demo keeps its own dish and no ♥.
  const HEART =
    '<svg viewBox="0 0 24 24" width="11" height="11"><path fill="currentColor" d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z"/></svg>';
  const paintSaved = (block) => {
    if (!block.hasAttribute('data-vpb-likes')) return;
    const api = window.vhpPlannerLikes;
    let items = [];
    try {
      items = api ? api.likeItems(api.read()) || [] : [];
    } catch (e) {
      items = [];
    }
    const main = [...items].reverse().find((item) => (item.slot || 'main') === 'main' && item.image);
    if (!main) return;
    const cell = block.querySelector('.vpb__cell[data-vpb-focus]');
    const meal = block.querySelector('.vpb__meal[data-vpb-focus]');
    if (cell) {
      let img = cell.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        img.width = 80;
        img.height = 80;
        cell.prepend(img);
      }
      img.src = main.image;
      cell.classList.add('is-hers');
      if (!cell.querySelector('.vpb__heart')) cell.insertAdjacentHTML('beforeend', `<span class="vpb__heart">${HEART}</span>`);
    }
    if (meal) {
      meal.classList.add('is-hers');
      const img = meal.querySelector('.vpb__meal-card--a img');
      if (img) img.src = main.image;
      const name = meal.querySelector('.vpb__meal-card--a .vpb__meal-name');
      if (name) name.textContent = main.title || name.textContent;
    }
    const picked = block.querySelector('[data-vpb-picked]');
    if (picked) picked.hidden = false;
  };
  window.vhpPlannerBlock = { paintSaved };
})();
